import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SupplierQualification } from '../../lib/api';
import type { ResponseRow } from './responseRows';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Building2, ChevronDown, ChevronRight, Eye, AlertTriangle, Info } from 'lucide-react';
import { useFormatters } from '../../contexts/PreferencesContext';
import { statusConfig } from './statusConfig';
import { groupQualificationsBySupplier, summariseGroup } from './groupBySupplier';

interface ListViewProps {
  filteredQualifications: SupplierQualification[];
}

// Customer review 11 Sep (V10): how the two derived columns are worked out,
// in the words of the code that derives them. Score comes from
// FilledDataset.calculate_score (points awarded / points available across
// is_scored questions; None when nothing is scored); Risk is the riskLevel
// expression in the row below (passed=false -> High, <70% -> Medium, else
// Low; blank when unscored). Keep all three in step.
const SCORE_LEGEND =
  'Score: points awarded across the scored questions, as a percentage of the ' +
  'points available. Blank when the template has no scored questions or none ' +
  'has been scored yet.';
export const RISK_MEDIUM_BELOW_PERCENT = 70;
const RISK_LEGEND =
  'Risk: High when the questionnaire failed its pass mark; Medium when it ' +
  `passed below ${RISK_MEDIUM_BELOW_PERCENT}%; Low at ${RISK_MEDIUM_BELOW_PERCENT}% or above; ` +
  'blank when not scored or not yet submitted.';

function HeaderLegend({ label, legend }: { label: string; legend: string }) {
  return (
    <span className="inline-flex items-center justify-center gap-1">
      {label}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            aria-label={`How ${label} is worked out`}
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs font-normal text-left">
          {legend}
        </TooltipContent>
      </Tooltip>
    </span>
  );
}

/* ============ LIST VIEW ============ */
export default function ListView({ filteredQualifications }: ListViewProps) {
  const navigate = useNavigate();
  const { formatDate } = useFormatters();
  // Collapsed by exception: every group starts open, so grouping never hides a
  // response from someone scanning the page.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const groups = useMemo(
    () => groupQualificationsBySupplier(filteredQualifications),
    [filteredQualifications]
  );

  const toggle = (key: string) => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <TooltipProvider>
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">
            Supplier Responses ({filteredQualifications.length})
          </h2>
          <p className="text-sm text-muted-foreground">
            {groups.length === 1
              ? 'Track supplier questionnaire completion and scores'
              : `Across ${groups.length} suppliers — questionnaire completion and scores`}
          </p>
        </div>
      </div>

      {/* List View Table */}
      <div className="overflow-x-auto border rounded-lg shadow-sm">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b-2 border-gray-300 bg-muted/50">
              {/* No Supplier column — the group header row carries the name,
                  so a column repeating it would be empty on every row. */}
              <th className="p-3 text-left font-semibold text-sm">Questionnaire</th>
              <th className="p-3 text-left font-semibold text-sm">Status</th>
              <th className="p-3 text-center font-semibold text-sm">Completion</th>
              <th className="p-3 text-center font-semibold text-sm">
                <HeaderLegend label="Score" legend={SCORE_LEGEND} />
              </th>
              <th className="p-3 text-left font-semibold text-sm">Date Sent</th>
              <th className="p-3 text-left font-semibold text-sm">Expiry Date</th>
              <th className="p-3 text-center font-semibold text-sm">
                <HeaderLegend label="Risk" legend={RISK_LEGEND} />
              </th>
              <th className="p-3 text-center font-semibold text-sm">Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const isCollapsed = collapsed.has(group.key);
              return (
              <Fragment key={group.key}>
              {/* One header per supplier. The flat table repeated the name on
                  every row, so a supplier with three questionnaires read as
                  three unrelated suppliers and nothing said how far any one of
                  them had actually got. */}
              <tr
                className="border-b bg-muted/40 cursor-pointer hover:bg-muted/60 transition-colors"
                onClick={() => toggle(group.key)}
              >
                <td colSpan={8} className="p-3">
                  <div className="flex items-center gap-2">
                    {isCollapsed
                      ? <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
                    <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="font-semibold text-sm">{group.supplierName}</span>
                    <span className="text-xs text-muted-foreground">
                      {summariseGroup(group)}
                    </span>
                  </div>
                </td>
              </tr>
              {!isCollapsed && group.qualifications.map((qual, idx) => {
              const statusConf = statusConfig[qual.status] || statusConfig['invited'];
              const isOverdue = qual.expires_at && new Date(qual.expires_at) < new Date() && qual.status !== 'approved' && qual.status !== 'rejected';
              // No score means nothing has been assessed — not a failure. It used
              // to paint an unscored, approved questionnaire as 0% / High risk.
              const riskLevel: 'high' | 'medium' | 'low' | null = qual.score_percentage == null
                ? null
                : qual.passed === false ? 'high' : qual.score_percentage < RISK_MEDIUM_BELOW_PERCENT ? 'medium' : 'low';
              return (
                <tr
                  key={qual.id}
                  className={`border-b hover:bg-muted/30 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-muted/10'}`}
                >
                  <td className="p-3 pl-10">
                    <div className="text-sm font-medium flex items-center gap-2">
                      {qual.template_name}
                      {(qual as Partial<ResponseRow>).kind === 'onboarding' && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">Onboarding</Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge
                      className="text-xs"
                      style={{ backgroundColor: statusConf.dotColor, color: '#fff' }}
                    >
                      {statusConf.label}
                    </Badge>
                    {isOverdue && (
                      <Badge variant="destructive" className="ml-1 text-xs">
                        Overdue
                      </Badge>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${qual.completion_percentage || 0}%` }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {qual.completion_percentage || 0}%
                      </span>
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    {qual.score_percentage != null ? (
                      <span className={`text-sm font-medium ${qual.passed ? 'text-green-600' : qual.passed === false ? 'text-red-600' : ''}`}>
                        {qual.score_percentage}%
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground" title="No answers have been scored yet">Not scored</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="text-sm text-muted-foreground">
                      {qual.invited_at ? formatDate(qual.invited_at) : '—'}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                      {qual.expires_at ? formatDate(qual.expires_at) : '—'}
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    {(qual.status === 'approved' || qual.status === 'submitted' || qual.status === 'under_review') && riskLevel ? (
                      <Badge
                        variant={riskLevel === 'high' ? 'destructive' : riskLevel === 'medium' ? 'secondary' : 'outline'}
                        className="text-xs"
                      >
                        {riskLevel === 'high' ? (
                          <><AlertTriangle className="h-3 w-3 mr-1" />High</>
                        ) : riskLevel === 'medium' ? (
                          'Medium'
                        ) : (
                          'Low'
                        )}
                      </Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate((qual as Partial<ResponseRow>).review_href ?? `/intelligence/qualifications/${qual.id}`)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </td>
                </tr>
              );
            })}
              </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
    </TooltipProvider>
  );
}
